import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

/**
 * ReferencePreview component - Displays a reading preview of reference annotations
 * with markdown formatting support
 */
function ReferencePreview({ reference, onToggleToBibtex }) {
  // Check if there's any content to display
  const hasComment = reference.comment && reference.comment.trim();
  const hasMethods = reference.methods && reference.methods.trim();
  const hasFocus = reference.focus && reference.focus.trim();
  const hasKeyFindings = reference.key_findings && reference.key_findings.trim();

  const hasAnyContent = hasComment || hasMethods || hasFocus || hasKeyFindings;

  // Custom components for markdown rendering
  const markdownComponents = {
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <SyntaxHighlighter
          style={oneDark}
          language={match[1]}
          PreTag="div"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      );
    },
    a({ node, children, href, ...props }) {
      // External links open in new tab
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
          {children}
        </a>
      );
    },
  };

  return (
    <div className="preview-container">
      <div className="preview-header">
        <h3 className="preview-title">Reading Preview</h3>
        <button
          className="preview-toggle-btn"
          onClick={(e) => {
            e.stopPropagation();
            onToggleToBibtex();
          }}
          title="Show BibTeX"
        >
          Show BibTeX →
        </button>
      </div>

      {!hasAnyContent && (
        <div className="preview-empty-state">
          No reading notes available. Click 'Edit Notes' to add comments, methods, focus, or key findings.
        </div>
      )}

      {hasComment && (
        <div className="preview-section">
          <div className="preview-section-title">Comment</div>
          <div className="preview-markdown">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {reference.comment}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {hasMethods && (
        <div className="preview-section">
          <div className="preview-section-title">Methods</div>
          <div className="preview-markdown">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {reference.methods}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {hasFocus && (
        <div className="preview-section">
          <div className="preview-section-title">Focus</div>
          <div className="preview-markdown">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {reference.focus}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {hasKeyFindings && (
        <div className="preview-section">
          <div className="preview-section-title">Key Findings</div>
          <div className="preview-markdown">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {reference.key_findings}
            </ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReferencePreview;
